import { useStore } from "@nanostores/react"
import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useIsHydrated } from "shared/lib/react/useIsHydrated"

import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { DEFAULT_FUZZY_SEARCH_OPTIONS } from "shared/constants/search"
import { NEW_PROJECT_OVERLAY } from "shared/context/overlay/types"
import { isEmpty } from "shared/lib/arrayUtils"
import { useDebounce } from "shared/lib/performance/useDebounce"
import { useUrlParamStore } from "shared/lib/url/useUrlParams"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"
import { useFuzzySearch } from "shared/lib/useFuzzySearch"
import { useOverlay } from "shared/lib/useOverlay"
import { Project } from "shared/types/Project"
import { SortOrder } from "shared/types/SortOrder"

import { useProjects } from "entities/projects/api/useProjects"
import { useAllProjectPermissionsQuery } from "entities/projects/graphql/queries.generated"
import {
  ProjectSortKey,
  useProjectSorting
} from "entities/projects/model/useProjectSorting"
import { useProjectActionHandlers } from "features/project-management/model/useProjectActionHandlers"
import {
  projectsOverviewFilterStore,
  setProjectsOverviewFilters
} from "pages/projects-overview-page/model/projects-overview-store"

import { ProjectsOverviewPageModel } from "../ProjectsOverviewPageView"
import { projectsFilterFormDataSchema } from "../schema/projectsFilterFormDataSchema"
import { useProjectPhaseFilterOptions } from "./useProjectPhaseFilterOptions"

function getProjectTitle(project: Project) {
  return project.title
}

export function useProjectsOverviewPageModel(): ProjectsOverviewPageModel {
  const { isAuthenticated } = useAuth()
  const filterState = useStore(projectsOverviewFilterStore)
  const [searchParams] = useSearchParams()
  const isHydrated = useIsHydrated()
  const { projects, loading: isLoading } = useProjects()
  const { onOpen: openProject } = useProjectActionHandlers()
  const { loading: isLoadingPermissions } = useAllProjectPermissionsQuery({
    skip: !isAuthenticated
  })

  const { openOverlay } = useOverlay(NEW_PROJECT_OVERLAY)
  const { projects: isProjectsServiceAvailable } = useAPIHealth()
  const { options } = useProjectPhaseFilterOptions()

  const { values, setValues } = useUrlParamStore(projectsFilterFormDataSchema, {
    state: filterState,
    setState: setProjectsOverviewFilters
  })

  // Seeding the debounce with the term from the URL lets a page that is opened with
  // a search show its results right away. The delay only exists to keep typing from
  // re-searching the whole project list on every keystroke.
  const debouncedSearchTerm = useDebounce(
    values.searchTerm,
    400,
    searchParams.get("searchTerm")
  )

  const debouncedFilterState = useMemo(
    () => ({
      ...values,
      // The seeded term is browser-only state and must not reach the first render.
      searchTerm: isHydrated ? debouncedSearchTerm : null
    }),
    [values, debouncedSearchTerm, isHydrated]
  )

  const handleOpenNewProjectOverlay = useEvent(() => {
    openOverlay({ navigateAfterSubmit: true })
  })

  const fuzzySearch = useFuzzySearch<Project>({
    getSearchableString: getProjectTitle,
    options: DEFAULT_FUZZY_SEARCH_OPTIONS
  })

  const filteredProjects = useMemo(() => {
    const phaseFilteredProjects =
      projects?.filter(
        project =>
          !debouncedFilterState.projectPhase ||
          isEmpty(debouncedFilterState.projectPhase) ||
          (project.phase?.value &&
            debouncedFilterState.projectPhase.includes(project.phase.value))
      ) || []

    return fuzzySearch(phaseFilteredProjects, debouncedFilterState.searchTerm)
  }, [
    projects,
    debouncedFilterState.projectPhase,
    debouncedFilterState.searchTerm,
    fuzzySearch
  ])

  const { validatedSortKey, validatedSortOrder } = useMemo(() => {
    const [sortKey = ProjectSortKey.UPDATED_AT, sortOrder = SortOrder.ASC] =
      debouncedFilterState.sort?.split(":") || []

    return {
      validatedSortKey: Object.values(ProjectSortKey).includes(
        sortKey as ProjectSortKey
      )
        ? (sortKey as ProjectSortKey)
        : ProjectSortKey.UPDATED_AT,
      validatedSortOrder: Object.values(SortOrder).includes(
        sortOrder as SortOrder
      )
        ? (sortOrder as SortOrder)
        : SortOrder.DESC
    }
  }, [debouncedFilterState.sort])

  const sortedProjects = useProjectSorting({
    projects: filteredProjects,
    sortKey: validatedSortKey,
    sortOrder: validatedSortOrder
  })

  const hasFilter =
    !isEmpty(debouncedFilterState?.projectPhase) ||
    !!debouncedFilterState.searchTerm

  return {
    isLoading: isLoading || isLoadingPermissions,
    projectListModel: {
      projects: sortedProjects,
      isProjectsServiceAvailable,
      hasFilter,
      isLoading: isLoading || isLoadingPermissions,
      openProject
    },
    controlsModel: {
      defaultFormData: values,
      projectPhaseFilterOptions: options,
      isDisabled: isLoading,
      onChange: setValues
    },
    isProjectsServiceAvailable,
    onNewProject: handleOpenNewProjectOverlay
  }
}
